import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { useEffect } from 'react';
import { isEmpty, omit } from 'lodash';
import { useHomeModals, ModalType } from './hooks/useHomeModals';
import { BusinessModel, Influencer } from '@playbook-technologies/common-enums';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  Feedback,
  getLinks,
  toFeedbackData,
  CreatorLinksType,
  getHomeCta,
  getOTPPrograms,
  getSubscriptionPrograms,
  getAfterDesc,
  externalUrls,
} from './mappers';
import { ButtonColor } from '@design-system/components/button/button.css';
import { PATHS } from '@client/routes';
import { navigate } from '@client/new/helpers/history';
import { Collections, CollectionType } from './home.domain';
import toast from 'react-hot-toast';
import { useHomeModel } from './home.model';
import { trackEvent, usePublishedProducts } from '@services/tracking';
import { getDmEnabled } from '@client/new/common/features.helpers';
import {
  useGuideModel,
  Checklist,
  Strategy,
} from '@client/new/pages/creator/guide/model';
import { browserHistory } from 'react-router';

export type MissingOnboardingStepKey =
  | 'profile'
  | 'paymentMethod'
  | 'collectionPublished';

export type MissingOnboardingProfileField =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'title'
  | 'instagram'
  | 'profileLink'
  | 'avatar';

export type MissingOnboardingStep = {
  key: MissingOnboardingStepKey;
  subItems?: MissingOnboardingProfileField[];
};

export type HomeVM = {
  dispatch: HomeVMVDispatcher;
  headline: {
    title: string;
    subtitle?: string;
    link?: string;
    cta?: Array<{
      icon: IconProp;
      onClick: () => void;
      content?: string;
      className?: string;
      disabled?: boolean;
      color?: ButtonColor;
    }>;
    missingOnboardingSteps: MissingOnboardingStep[];
  };
  isOnboarding: boolean;
  isLive: boolean;
  influencerId: number;
  uid: string;
  showOnboardingCards: boolean;
  hasStrategy: boolean;
  goLiveModal: {
    isOpen: boolean;
    onClose: () => void;
    version: CollectionType | 'both';
    goLive: () => void;
    inProgress: boolean;
  };
  liveSuccessModal: {
    isOpen: boolean;
    onClose: () => void;
    version: CollectionType | 'both';
    influencerId: number;
  };
  feedback: {
    show: boolean;
    data: Feedback;
    dmEnabled: boolean;
  };
  linksModal: {
    showLinks: boolean;
    links: CreatorLinksType;
    afterDesc: React.ReactNode;
    isOpen: boolean;
  };
  openedModal: ModalType;
  guideLoading: boolean;
  guideChecklistPreview: {
    checklist: Checklist | null;
    checklistNumber: number;
    isActiveChecklistCompleted: boolean;
    influencerId: number;
    onTaskToggle: (taskId: string, completed: boolean) => void;
    onOpenGuide: () => void;
    onOpenTaskModal: (templateId: string) => void;
    onStartStrategy: () => void;
  };
  guideHeader: {
    strategy: Strategy | null;
    completedChecklistsCount: number;
    totalChecklistsCount: number;
    totalEstimatedTime: string;
    canGoLive: boolean;
    onGoLive: () => void;
    onReviewStrategy: () => void;
  };
  freeOffersModal: {
    isOpen: boolean;
    close: () => void;
    redirect: () => void;
  };
  // track: (type: HomeScreenEvent['type']) => void;
};

export type PayloadMap = {
  closeModal: null;
  openGoLiveModal: null;
  goLive: null;
  setupOffer: null;
  openLinksModal: null;
  openModal: ModalType;
  modalOpened: ModalType;
  challengeJoined: null;
  communityJoined: null;
  closeFreeOffersModal: null;
};

export type HomeVMActions = Actions<PayloadMap>;
export type HomeVMVDispatcher = FSpread<PayloadMap>;

export const useHomeViewModel = (p: {
  influencerId?: string;
}): Async<HomeVM> => {
  const asyncModel = useHomeModel(p);
  const publishedProducts = usePublishedProducts(p);

  const asyncModals = useHomeModals({
    model: asyncModel,
  });

  const influencerIdForGuide =
    asyncModel.type === 'fetched' ? asyncModel.value.influencerId : 0;

  const asyncGuideModel = useGuideModel(influencerIdForGuide, null);

  useEffect(() => {
    if (publishedProducts.type !== 'fetched') return;
    if (asyncModel.type !== 'fetched') return;

    const creatorWentLive = !asyncModel.value.creatorWentLive;
    if (creatorWentLive) {
      trackEvent({
        type: 'Creator Is Live',
        payload: {
          'Product Type': publishedProducts.value.publishedProductType,
          'Collection Type': publishedProducts.value.publishedCollectionType,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncModel.type, publishedProducts]);

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (publishedProducts.type !== 'fetched') return publishedProducts;
  if (asyncModals.type !== 'fetched') return asyncModals;

  const model = asyncModel.value;
  const modals = asyncModals.value;

  const dispatch: HomeVMVDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as HomeVMActions;
    switch (action.type) {
      case 'challengeJoined': {
        trackEvent({
          type: 'Onboarding Tile Tapped',
          payload: {
            Title: 'Join the Black Friday Launch Challenge',
            Source: isLive ? 'Post-Launch' : 'Pre-Launch',
          },
        });
        window.open(externalUrls['blackFridayChallenge'], '_blank');
        return null;
      }
      case 'communityJoined': {
        trackEvent({
          type: 'Onboarding Tile Tapped',
          payload: {
            Title: 'Join our FREE Creator Community',
            Source: isLive ? 'Post-Launch' : 'Pre-Launch',
          },
        });
        window.open(externalUrls['community'], '_blank');
        return null;
      }
      case 'modalOpened':
        if (action.payload === 'QrCode')
          trackEvent({
            type: 'Creator app downloaded',
          });

        return null;
      case 'openModal':
        modals.openModal(action.payload);
        return null;
      case 'openLinksModal':
        modals.openModal('Links');
        return null;
      case 'closeModal':
        modals.closeModal();
        return null;
      case 'setupOffer':
        redirectToOffers();
        return null;
      case 'openGoLiveModal':
        trackEvent({
          type: 'Go Live Tapped',
          payload: {
            'Product Type': publishedProducts.value.publishedProductType,
            'Collection Type': publishedProducts.value.publishedCollectionType,
          },
        });
        modals.openModal('GoLive');
        return null;
      case 'goLive':
        model
          .goLive()
          .then(() => modals.closeModal())
          .catch((err) => toast.error(err.message));
        return null;
      case 'closeFreeOffersModal':
        modals.closeFreeOffersModal();
        return null;
    }
  };

  const influencer = model.data?.creator;
  const isLive = model.data?.creator?.mode === Influencer.Mode.Live.value;
  const onboardingList = omit(model.data?.onboardingListData, '__typename');
  const customURL = influencer?.customURL || undefined;
  const profileDeepLink = influencer?.profileDeepLink || undefined;
  const otpPrograms: Collections = getOTPPrograms(model.data.collections);
  const subscriptionPrograms: Collections = getSubscriptionPrograms(
    model.data.collections,
  );
  const isBundleCreator =
    model.data.creator?.businessModel === BusinessModel.Bundle;

  const linksType =
    otpPrograms.length > 0 && subscriptionPrograms.length > 0
      ? 'both'
      : otpPrograms.length > 0
      ? 'otp'
      : 'subscription';

  const hasLinks = otpPrograms.length > 0 || subscriptionPrograms.length > 0;

  const dmEnabled = getDmEnabled(influencer?.features);

  const redirectToOffers = () => {
    browserHistory.push(PATHS.BUNDLES(model.influencerId));
  };

  const canGoLive = Object.values(onboardingList).every((i) => i === true);
  const onboardingData = model.data?.onboardingListData;
  const missingProfileFields: MissingOnboardingProfileField[] = [
    !influencer?.firstName && 'firstName',
    !influencer?.lastName && 'lastName',
    !influencer?.email && 'email',
    !influencer?.title && 'title',
    !influencer?.profileInstagram && 'instagram',
    !influencer?.profileDeepLink && 'profileLink',
    !(influencer?.mediaAvatar?.cdn01 || influencer?.mediaAvatar?.image) &&
      'avatar',
  ].filter((f): f is MissingOnboardingProfileField => Boolean(f));
  const missingOnboardingSteps: MissingOnboardingStep[] = [
    ...(onboardingData?.profileReady
      ? []
      : [{ key: 'profile' as const, subItems: missingProfileFields }]),
    ...(onboardingData?.paymentMethodReady
      ? []
      : [{ key: 'paymentMethod' as const }]),
    ...(onboardingData?.collectionPublishedReady
      ? []
      : [{ key: 'collectionPublished' as const }]),
  ];
  const goLiveVersion =
    !isEmpty(otpPrograms) && !isEmpty(subscriptionPrograms)
      ? 'both'
      : !isEmpty(subscriptionPrograms)
      ? 'subscription'
      : 'otp';

  // Get active checklist for preview (first incomplete or first checklist)
  const guideChecklists =
    asyncGuideModel.type === 'fetched' ? asyncGuideModel.value.checklists : [];
  const guideStrategy =
    asyncGuideModel.type === 'fetched' ? asyncGuideModel.value.strategy : null;
  const activeGuideChecklist =
    guideChecklists.find((c) => c.isActive) ??
    guideChecklists.find((c) => c.tasks.some((t) => !t.isChecked)) ??
    guideChecklists[0] ??
    null;
  const activeChecklistIndex = activeGuideChecklist
    ? guideChecklists.findIndex((c) => c.id === activeGuideChecklist.id)
    : 0;

  // Guide header stats
  const completedChecklistsCount = guideChecklists.filter((c) =>
    c.tasks.every((t) => t.isChecked),
  ).length;
  const totalChecklistsCount = guideChecklists.length;
  const totalEstimatedTime = guideStrategy?.launchTimeLabel ?? '-';
  const isActiveChecklistCompleted = activeGuideChecklist
    ? activeGuideChecklist.tasks.every((t) => t.isChecked)
    : false;
  const handleTaskToggle = (taskId: string, completed: boolean) => {
    if (asyncGuideModel.type === 'fetched') {
      asyncGuideModel.value.toggleTask({ taskId, completed }).catch((err) => {
        toast.error(err.message);
      });
    }
  };

  const handleOpenGuide = () => {
    navigate(PATHS.GUIDE(model.influencerId));
  };

  const getOnboardingPath = (step?: string): string => {
    const basePath = p.influencerId
      ? PATHS.ONBOARDING_ADMIN(model.influencerId)
      : PATHS.ONBOARDING;
    return step ? `${basePath}?step=${step}` : basePath;
  };

  const handleReviewStrategy = () => {
    navigate(getOnboardingPath('strategy'));
  };

  return {
    type: 'fetched',
    value: {
      openedModal: modals.modalType,
      dispatch,
      headline: {
        title: `Welcome ${influencer?.firstName || ''} 👋`,
        subtitle: isLive
          ? undefined
          : 'Continue with your next tasks to unlock the "Go live" button and start making 💸💸💸',
        link: isLive
          ? 'https://docs.google.com/document/d/1ufQNOy-6-v5VfnPDrDEVJqIPCV_ILOtPY-wGM2ZakbE/preview'
          : undefined,
        cta: getHomeCta({ isLive, dispatch, canGoLive }),
        missingOnboardingSteps,
      },
      // track: trackMixPanel(publishedProducts.value),
      isOnboarding:
        model.data?.creator?.mode === Influencer.Mode.Onboarding.value,
      isLive,
      influencerId: model.influencerId,
      uid: model.uid,
      guideLoading:
        asyncGuideModel.type === 'fetching' ||
        asyncGuideModel.type === 'not-fetched',
      showOnboardingCards:
        asyncGuideModel.type === 'fetched' &&
        !model.isGuideVisible &&
        guideChecklists.length === 0,
      hasStrategy: !!guideStrategy,
      linksModal: {
        showLinks: hasLinks && isLive,
        isOpen: modals.isModalOpen('Links'),
        links: getLinks(linksType, isBundleCreator, customURL, profileDeepLink),
        afterDesc: getAfterDesc(linksType, model.influencerId),
      },
      goLiveModal: {
        isOpen: modals.isModalOpen('GoLive'),
        onClose: () => dispatch('closeModal'),
        version: goLiveVersion,
        goLive: () => dispatch('goLive'),
        inProgress: model.goLiveProgress,
      },
      liveSuccessModal: {
        isOpen: modals.isModalOpen('LiveSuccess'),
        onClose: () => dispatch('closeModal'),
        version: goLiveVersion,
        influencerId: model.influencerId,
      },
      feedback: {
        show: isLive,
        data: toFeedbackData(model.data.searchComments),
        dmEnabled,
      },
      guideChecklistPreview: {
        checklist: activeGuideChecklist,
        checklistNumber: activeChecklistIndex + 1,
        isActiveChecklistCompleted,
        influencerId: model.influencerId,
        onTaskToggle: handleTaskToggle,
        onOpenGuide: handleOpenGuide,
        onOpenTaskModal: () => {
          handleOpenGuide();
        },
        onStartStrategy: () => {
          navigate(getOnboardingPath('strategy'));
        },
      },
      guideHeader: {
        strategy: guideStrategy,
        completedChecklistsCount,
        totalChecklistsCount,
        totalEstimatedTime,
        canGoLive,
        onGoLive: () => dispatch('openGoLiveModal'),
        onReviewStrategy: handleReviewStrategy,
      },
      freeOffersModal: {
        isOpen: modals.isModalOpen('FreeOffers'),
        close: () => dispatch('closeFreeOffersModal'),
        redirect: () => redirectToOffers(),
      },
    },
  };
};
